import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ConfirmationDialogOptions, ConfirmationDialogService } from "@polk_county_public_schools/ngx-pcps-dialogs/confirmation";
import { SessionStorage } from "ngx-webstorage";
import { BehaviorSubject } from "rxjs";
import { Router } from "@angular/router";
import { SnackBarService } from "@polk_county_public_schools/ngx-pcps-dialogs/snackbar";

@Injectable({
    providedIn: 'root'
})
export class UserService {
    @SessionStorage() private SessionUser: UserInfo;
    UserSubject: BehaviorSubject<UserInfo> = new BehaviorSubject<UserInfo>(null);
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly confirmDlgSvc = inject(ConfirmationDialogService);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly router = inject(Router);

    get User() {
        return this.SessionUser;
    }

    set User(_ui: UserInfo) {

    }

    constructor() {
        //used to get session variable on page refresh
        if (this.SessionUser != null) {
            this.User = this.SessionUser;
            this.UserSubject.next(this.SessionUser);
        }
    }

    ClearUser() {
        this.User = null;
        this.SessionUser = null;
        this.UserSubject.next(null);
    }

    SetUser(user: UserInfo) {
        //set UserService property
        this.User = user;
        //set session variable
        this.SessionUser = user;
        //update user subject
        this.UserSubject.next(user);
    }

    UpdateAuthToken(AuthToken: any) {
        // If the authtoken has changed, update the user record
        if (AuthToken != this.User.AuthToken) {

            // To trigger proper session storage, we need to create a new object reference, rather than just updating our existing User object
            let newUser: UserInfo = { ...this.User };

            // Update the AuthToken field
            newUser.AuthToken = AuthToken;

            // Now, persist it to session storage and then broadcast it for listeners
            this.SetUser(newUser);
        }
    }

    logout(confirmation?: boolean, reason?: number) {
        if (confirmation === true) {
            // Pop up a confirmation box to confirm desire to logout.
            let opts: ConfirmationDialogOptions = {
                disableClose: true,
                TitleText: 'Confirmation Requested',
                MessageText:
                    'You are about to log out of the site. Are you sure you wish to exit at this time?',
                OKText: 'Yes - Log Out',
                CancelText: 'No - Stay Logged In',
            };

            let dialog = this.confirmDlgSvc.Open(opts);

            dialog
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((response) => {
                    if (response === true) {
                        // User is opting to log out. Proceed.

                        // Null out the current user.
                        this.ClearUser();

                        // Snackbar the logout if this was user initiated (reason null or 0)
                        if (reason == null || reason == 0) {
                            this.snackBarSvc.success(
                                'Successfully logged out',
                                5000
                            );
                        }

                        // Navigate to the login state.
                        this.router.navigate(['/login']);
                    }
                });
        } else {
            // No dialog needed. Exit out.

            // Null out the current user.
            this.ClearUser();

            // Snackbar the logout if this was user initiated (reason null or 0)
            if (reason == null || reason == 0) {
                this.snackBarSvc.success('Successfully logged out', 5000);
            }
            else if (reason === 1) {
                this.snackBarSvc.error('Session has expired. Please log back in.', 10000);
            }

            // Navigate to the login state.
            this.router.navigate(['/login']);
        }
    }
}

export class UserInfo {
    AccountName: string;
    AuthToken: string;
    UserID: number;
    SAPID: string;
    StudentID: string;
    FirstName: string;
    LastName: string;
    RoleBooleans: RoleBoolean;
    RoleList: string[];
    CurrentRole: string;
    Impersonation: boolean;
    Training: boolean;
    IsCharter: boolean;
}

export class RoleBoolean {
    IsDeveloper: boolean;
    IsAdmin: boolean;
    IsStudent: boolean;
}